import type { Capability } from '@boud/contracts';
import type { IconName } from '../lib/icons';

export interface NavItem {
  /** Translation key, kept identical to the prototype's `T` map. */
  key: string;
  path: string;
  icon: IconName;
  /**
   * Coarse gate only. FRS §4.1: "Navigation access is a coarse gate;
   * capabilities are the authoritative permission check and must be enforced
   * server-side on every request." Hiding a nav item is a courtesy to the user,
   * never a security boundary.
   */
  capability?: Capability;
}

export interface NavGroup {
  labelKey: string;
  items: NavItem[];
}

/**
 * MVP navigation (FRS §2.3).
 *
 * The prototype also renders a Property group (Tenants & Leases, Billing) and a
 * Purchase Requests entry. Those belong to Phase 2 (§12.4) and "must not be
 * built, routed, or rendered in the MVP" — so they are absent here, not hidden
 * behind a flag. A flag would still ship the route.
 *
 * Settings is absent for a different reason: it is not a module. It holds each
 * employee's own calendar connection alongside the platform's configuration, so
 * putting it under Administration would show an ordinary employee an
 * "Administration" heading for a screen that, to them, is entirely personal. It
 * is reached from the topbar instead, beside the other account controls.
 */
export const NAV_GROUPS: NavGroup[] = [
  {
    labelKey: 'nav_ops',
    items: [
      { key: 'dashboard', path: '/dashboard', icon: 'dashboard' },
      { key: 'complaints', path: '/support', icon: 'chat' },
      { key: 'bookings', path: '/bookings', icon: 'calendar' },
      { key: 'visitors', path: '/visitors', icon: 'visitor', capability: 'Visitors.Manage' },
    ],
  },
  {
    labelKey: 'nav_admin',
    items: [
      { key: 'companies', path: '/companies', icon: 'building', capability: 'Companies.Manage' },
      { key: 'employees', path: '/employees', icon: 'people', capability: 'Employees.View' },
      { key: 'rooms', path: '/rooms', icon: 'room', capability: 'Rooms.Manage' },
      /*
       * `Audit.Read`, not `Activity.ViewRecent`. The two are different things
       * and conflating them widens access: `Activity.ViewRecent` gates the
       * Dashboard's recent-activity feed, which Reception and Manager both hold,
       * while the Security Log is the full audit trail — Super Admin only, per
       * ADR 0005.
       */
      { key: 'security', path: '/security', icon: 'shield', capability: 'Audit.Read' },
    ],
  },
];

export const ALL_NAV_ITEMS: NavItem[] = NAV_GROUPS.flatMap((group) => group.items);
